import type { RecurringExpense } from '@/types'
import type { IRecurringExpenseRepository } from './recurringExpenseTypes'
import { isRecurringExpensesArray } from './validators'

const STORAGE_KEY = 'debtos:recurringExpenses:v1'

export class LocalStorageRecurringExpenseRepo implements IRecurringExpenseRepository {
  private read(): RecurringExpense[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return []
      const parsed: unknown = JSON.parse(raw)
      if (!isRecurringExpensesArray(parsed)) {
        console.warn(
          `[debtos] Malformed payload at ${STORAGE_KEY}; resetting to default. ` +
            'Most likely caused by a restore from an incompatible backup.'
        )
        return []
      }
      return parsed
    } catch {
      return []
    }
  }

  /**
   * Throws on any `setItem` failure (QuotaExceededError under Safari private
   * mode, near-full Android storage) so callers can surface the problem to the
   * user instead of letting in-memory state drift silently ahead of disk.
   */
  private write(items: RecurringExpense[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch (err) {
      throw new Error(
        `Failed to save to local storage (${STORAGE_KEY}): ` +
          (err instanceof Error ? err.message : String(err))
      )
    }
  }

  async getAll(): Promise<RecurringExpense[]> {
    return this.read()
  }

  async getById(id: string): Promise<RecurringExpense | null> {
    return this.read().find((e) => e.id === id) ?? null
  }

  async upsert(expense: RecurringExpense): Promise<RecurringExpense> {
    const items = this.read()
    const idx = items.findIndex((e) => e.id === expense.id)
    const updated = { ...expense, updatedAt: new Date().toISOString() }
    if (idx >= 0) {
      items[idx] = updated
    } else {
      items.push(updated)
    }
    this.write(items)
    return updated
  }

  async delete(id: string): Promise<void> {
    const items = this.read().filter((e) => e.id !== id)
    this.write(items)
  }

  async bulkReplace(items: RecurringExpense[]): Promise<void> {
    this.write(items)
  }
}
